import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, ArrowLeft, Mail } from "lucide-react";
import SEO from "../components/SEO";
import { formatApiError } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminForgotPasswordPage() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const trimmed = identifier.trim();
      if (!trimmed || trimmed.length < 3) {
        setError("Vul je e-mailadres of gebruikersnaam in.");
        setLoading(false);
        return;
      }
      await axios.post(
        `${BACKEND_URL}/api/admin/auth/forgot-password`,
        { email_or_username: trimmed },
        { headers: { "Content-Type": "application/json" } },
      );
      // Always show the same message regardless of result.
      setSubmitted(true);
    } catch (err) {
      // We still mask success/failure — only network/validation errors surface here.
      const status = err?.response?.status;
      if (status === 422) {
        setError("Vul een geldig e-mailadres of gebruikersnaam in.");
      } else if (status === 429) {
        setError("Te veel pogingen. Probeer het later opnieuw.");
      } else if (!err?.response) {
        setError("Geen verbinding met de server. Probeer het later opnieuw.");
      } else {
        setError(formatApiError(err) || "Er ging iets mis. Probeer het opnieuw.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Wachtwoord vergeten | 5999Cargo" description="Backoffice wachtwoord herstellen." />
      <section className="min-h-[80vh] flex items-center bg-[#F8F9FA]" data-testid="admin-forgot-password-page">
        <div className="max-w-md mx-auto w-full px-4">
          <div className="bg-white border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#082c50] flex items-center justify-center">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-chivo font-black text-xl text-[#082c50]">Wachtwoord vergeten</div>
                <div className="text-xs text-gray-500">5999Cargo Backoffice</div>
              </div>
            </div>

            {submitted ? (
              <div data-testid="admin-forgot-password-success" className="space-y-5">
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                      Als dit account bij ons bekend is, ontvangt u binnen enkele
                      minuten een e-mail met instructies om uw wachtwoord opnieuw in te stellen.
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  Geen e-mail ontvangen? Controleer ook je spamfolder. De link is 30 minuten geldig.
                </p>
                <Button
                  type="button"
                  data-testid="admin-forgot-password-back-to-login"
                  className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-sm py-6"
                  onClick={() => navigate("/admin/login")}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Terug naar inloggen
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-forgot-password-form">
                <p className="text-sm text-gray-600">
                  Vul je e-mailadres of gebruikersnaam in. Als het account bij ons bekend is sturen we
                  je een e-mail met een eenmalige link om je wachtwoord opnieuw in te stellen.
                </p>
                <div>
                  <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
                    E-mailadres of gebruikersnaam
                  </Label>
                  <Input
                    data-testid="admin-forgot-password-input"
                    type="text"
                    required
                    autoComplete="username"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="mt-1 rounded-xl border-gray-300"
                    placeholder="naam@5999cargo.nl"
                  />
                </div>

                {error && (
                  <div
                    data-testid="admin-forgot-password-error"
                    className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm"
                  >
                    {error}
                  </div>
                )}

                <Button
                  data-testid="admin-forgot-password-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-sm py-6"
                >
                  {loading ? "Bezig..." : "Resetlink aanvragen"}
                </Button>

                <div className="text-center pt-1">
                  <Link
                    to="/admin/login"
                    data-testid="admin-forgot-password-back-link"
                    className="text-xs text-[#1a5193] hover:underline font-chivo tracking-wide"
                  >
                    Terug naar inloggen
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
